import React, { useEffect, useState } from 'react'

import { Button, Modal, ModalProps, TextInputV3 } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { FormikProps, withFormik } from 'formik'
import { isEmpty } from 'ramda'
import { compose, withState } from 'recompose'
import styled from 'styled-components'

import { Body, Title } from 'components/Typography'
import { TestIDs } from 'test/utils/testIds'
import { style } from 'theme'
import Environment, { withEnv } from 'utils/environment'
import { phoneMask } from 'utils/text-masks/phone'

import { usePhoneSignatureModalTracking } from './events'
import { mapPropsToValues, validationSchema } from './form'
import { formatPhoneNumber, sanitize } from './utils'

type Props = {
  isOpen: boolean
  onNumberSubmit: ({ phoneNumber }: { phoneNumber: string }) => void
  onRequestClose: () => void
  handleNumberSubmit: ({ phoneNumber, message }: { phoneNumber: string; message?: string }) => void
  phoneNumber?: string
  onExit?: () => void
  formattedNumber?: string
  setFormattedNumber?: (phoneNumber: string) => void
  setSubmittedPhoneNumber?: (phoneNumber: string) => void
  submittedPhoneNumber?: string | null
  handleChange?: (e: React.SyntheticEvent) => void
}

type FormValues = {
  phoneNumber: string
}

type MergedProps = {
  env: Environment
  rawPhoneNumber: string
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
} & Props &
  ModalProps &
  FormikProps<FormValues>

const enhance = compose<MergedProps, Props>(
  withEnv,
  withState('formattedNumber', 'setFormattedNumber', null),
  withFormik<Props, FormValues>({
    handleSubmit: (values: FormValues, args) => {
      const { props } = args
      args.validateForm()
      props.setFormattedNumber(formatPhoneNumber(sanitize(values.phoneNumber)))
      props.handleNumberSubmit({
        phoneNumber: sanitize(values.phoneNumber),
        message:
          'Tap this link to sign your Mighty Oak debit card. Then, continue registering on your desktop.',
      })
    },
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    mapPropsToValues,
  }),
)

const StyledModal = styled(Modal)`
  width: 100%;

  &&& {
    padding: 0;
  }
`

const ModalTitle = styled(Title)`
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
`

const ModalBody = styled(Body)`
  text-align: center;
  font-size: 14px;
  line-height: 22px;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`

const InnerContent = styled.div`
  padding: 50px 40px;
`

const Form = styled.form`
  width: 100%;
  margin: 0;
  border: 0;
  padding: 0;
`

const PhoneNumberInput = styled(TextInputV3)`
  max-width: 315px;
  margin: 20px auto 0;
`

const ButtonsSection = styled.div`
  display: flex;
  width: 100%;
  height: 60px;
  border-top: 1px solid ${style(theme => theme.colors.ivory)};
  font-weight: 600;
`

const SecondaryButton = styled(Button).attrs({
  styleType: 'flat',
  secondary: true,
})`
  width: 50%;
  height: 100%;
  border-right: 1px solid ${style(theme => theme.colors.ivory)} !important;
`
const PrimaryButton = styled(Button).attrs({
  styleType: 'flat',
})`
  width: 50%;
  height: 100%;

  &:disabled {
    color: ${style(theme => theme.colors.stoneLight)};
  }
`
const PrimaryWideButton = styled(Button).attrs({
  styleType: 'flat',
})`
  width: 100%;
  height: 100%;
`

const StyledPhoneNumber = styled.span`
  color: ${style(theme => theme.colors.green)};
  font-weight: 600;
`

const PhoneModal: React.ComponentClass<Props> = enhance(
  ({
    errors,
    handleChange,
    handleSubmit,
    isOpen,
    onRequestClose,
    onExit,
    phoneNumber,
    values,
    formattedNumber,
  }) => {
    const track = usePhoneSignatureModalTracking()
    const { getRemoteString } = useRemoteStrings()
    const [hasSubmitted, setHasSubmitted] = useState(false)
    // TO-DO Add accessibilityProperties prop once signature-pad is accessible.
    useEffect(() => {
      if (isOpen) {
        track.containerViewed()
      }
    }, [isOpen])

    useEffect(() => {
      if (hasSubmitted) {
        track.sentContainerViewed()
      }
    }, [hasSubmitted])

    return (
      <StyledModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        onCloseComplete={() => setHasSubmitted(false)}
      >
        <Container data-testid={TestIDs.SIGN_YOUR_CARD.SIGN_WITH_PHONE_MODAL}>
          {!hasSubmitted ? (
            <Form>
              <InnerContent>
                <ModalTitle>
                  {getRemoteString({
                    key: 'registration.card_order.signature.sign_on_phone.header',
                  })}
                </ModalTitle>
                <ModalBody>
                  {getRemoteString({ key: 'registration.card_order.signature.sign_on_phone.body' })}
                </ModalBody>

                <PhoneNumberInput
                  label={{ value: 'Mobile number' }}
                  inputProps={{
                    name: 'phoneNumber',
                    value: values.phoneNumber,
                    type: 'tel',
                    id: 'phoneNumber',
                    onChange: handleChange,
                    mask: phoneMask,
                  }}
                  error={!!errors.phoneNumber}
                  errorText={errors.phoneNumber}
                />
              </InnerContent>
              <ButtonsSection>
                <SecondaryButton
                  onPress={() => {
                    track.cancelTapped()
                    onExit()
                  }}
                  data-testid={TestIDs.SIGN_YOUR_CARD.MODAL_CLOSE_BUTTON}
                >
                  {getRemoteString({
                    key: 'registration.card_order.signature.sign_on_phone.cancel_cta',
                  })}
                </SecondaryButton>
                <PrimaryButton
                  disabled={
                    (!isEmpty(errors) || values.phoneNumber.replace(/\D/g, '').length !== 10) &&
                    !phoneNumber
                  }
                  onPress={() => {
                    track.sendTapped()
                    setHasSubmitted(true)
                    handleSubmit()
                  }}
                  data-testid={TestIDs.SIGN_YOUR_CARD.SEND_LINK_BUTTON}
                >
                  {getRemoteString({
                    key: 'registration.card_order.signature.sign_on_phone.send_cta',
                  })}
                </PrimaryButton>
              </ButtonsSection>
            </Form>
          ) : (
            <>
              <InnerContent>
                <ModalTitle>On its way</ModalTitle>
                <ModalBody>
                  We sent a link to{' '}
                  <StyledPhoneNumber>
                    {formattedNumber ? formattedNumber : values.phoneNumber}
                  </StyledPhoneNumber>
                  . Depending on your carrier, it may take up to a minute to receive.
                </ModalBody>
              </InnerContent>
              <ButtonsSection>
                <PrimaryWideButton
                  onPress={() => {
                    track.sentButtonTapped()
                    onExit()
                  }}
                  data-testid={TestIDs.SIGN_YOUR_CARD.MODAL_CLOSE_BUTTON}
                >
                  Ok
                </PrimaryWideButton>
              </ButtonsSection>
            </>
          )}
        </Container>
      </StyledModal>
    )
  },
)

export default PhoneModal
